import {
  ADMIN_OUTLET_FETCH_ALL,
  ADMIN_OUTLET_FETCH_ALL_SUCCESS,
  ADMIN_OUTLET_FETCH_ALL_FAIL,
  ADMIN_OUTLET_ADD,
  ADMIN_OUTLET_ADD_SUCCESS,
  ADMIN_OUTLET_ADD_FAIL,
  ADMIN_OUTLET_UPDATE,
  ADMIN_OUTLET_UPDATE_SUCCESS,
  ADMIN_OUTLET_UPDATE_FAIL,
  ADMIN_OUTLET_DELETE,
  ADMIN_OUTLET_DELETE_SUCCESS,
  ADMIN_OUTLET_DELETE_FAIL,
  ADMIN_OUTLET_SET_SORT,
  ADMIN_OUTLET_SET_PAGE,
  ADMIN_OUTLET_SET_PAGE_SIZE
} from './constants';
import { createSelector } from 'reselect';
import {
  getFilterAttribute,
  getFilterOperator,
  getFilterValue
} from '../filterSelectors';
import { filterItemsFunctionCreator } from '../util/filterItems';
import { buildAddressString } from '../../util/address';

const initialState = {
  fetching: false,
  fetched: false,
  outlets: {},
  sortAttribute: 'name',
  sortAttributeInverted: false,
  addingNewUser: false,
  page: 1,
  pageSize: 20
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ADMIN_OUTLET_FETCH_ALL:
      return { ...state, fetching: true };
    case ADMIN_OUTLET_FETCH_ALL_SUCCESS: {
      const outlets = {};
      action.outlets.forEach(outlet => {
        outlet.addressString = buildAddressString(outlet);
        outlets[outlet.id] = outlet;
        outlets[outlet.id].saved = true;
      });
      return { ...state, fetching: false, fetched: true, outlets };
    }
    case ADMIN_OUTLET_FETCH_ALL_FAIL:
      return { ...state, fetching: false };
    case ADMIN_OUTLET_ADD: {
      const outlets = JSON.parse(JSON.stringify(state.outlets));
      outlets[action.outlet.id] = action.outlet;
      outlets[action.outlet.id].saving = true;
      return { ...state, outlets };
    }
    case ADMIN_OUTLET_ADD_SUCCESS: {
      const outlets = JSON.parse(JSON.stringify(state.outlets));
      delete outlets[action.outlet.id].saving;
      outlets[action.outlet.id].saved = true;
      return { ...state, outlets };
    }
    case ADMIN_OUTLET_ADD_FAIL: {
      const outlets = JSON.parse(JSON.stringify(state.outlets));
      delete outlets[action.outlet.id].saving;
      delete outlets[action.outlet.id].saved;
      return { ...state, outlets };
    }
    case ADMIN_OUTLET_UPDATE: {
      const outlets = JSON.parse(JSON.stringify(state.outlets));
      outlets[action.outlet.id] = action.outlet;
      outlets[action.outlet.id].saving = true;
      return { ...state, outlets };
    }
    case ADMIN_OUTLET_UPDATE_SUCCESS: {
      const outlets = JSON.parse(JSON.stringify(state.outlets));
      outlets[action.outlet.id].saved = true;
      delete outlets[action.outlet.id].saving;
      return { ...state, outlets };
    }
    case ADMIN_OUTLET_UPDATE_FAIL: {
      const outlets = JSON.parse(JSON.stringify(state.outlets));
      delete outlets[action.outlet.id].saving;
      return { ...state, outlets };
    }
    case ADMIN_OUTLET_DELETE: {
      const outlets = JSON.parse(JSON.stringify(state.outlets));
      delete outlets[action.outlet.id];
      return { ...state, outlets };
    }
    case ADMIN_OUTLET_DELETE_SUCCESS:
      return state;
    case ADMIN_OUTLET_DELETE_FAIL: {
      const outlets = JSON.parse(JSON.stringify(state.outlets));
      outlets[action.outlet.id] = action.outlet;
      return { ...state, outlets };
    }
    case ADMIN_OUTLET_SET_SORT: {
      const sortAttributeInverted =
        state.sortAttribute === action.sort ?
          !state.sortAttributeInverted : false;
      return { ...state, sortAttribute: action.sort, sortAttributeInverted };
    }
    case ADMIN_OUTLET_SET_PAGE:
      return { ...state, page: action.page };
    case ADMIN_OUTLET_SET_PAGE_SIZE:
      return { ...state, pageSize: action.size };
  }
  return state;
};

const fetching = state => state.outletAdmin.fetching;
const fetched = state => state.outletAdmin.fetched;
const outlets = state => state.outletAdmin.outlets;
const sortAttribute = state => state.outletAdmin.sortAttribute;
const sortAttributeInverted = state => state.outletAdmin.sortAttributeInverted;
const page = state => state.outletAdmin.page;
const pageSize = state => state.outletAdmin.pageSize;

export const getOutletPage = createSelector(
  page, page => page
);

export const getOutletPageSize = createSelector(
  pageSize, size => size
);

export const isFetchingOutlets = createSelector(
  fetching, fetching => fetching
);

export const hasFetchedOutlets = createSelector(
  fetched, fetched => fetched
);

export const getSavedOutlets = createSelector(
  outlets, outlets => Object.values(outlets).filter(outlet => outlet.saved)
);

const filterItems = filterItemsFunctionCreator([
  { attribute: 'name', operator: 'contains' },
  { attribute: 'addressString', operator: 'contains' }
]);

export const getFilteredOutlets = createSelector(
  [getSavedOutlets, getFilterAttribute, getFilterOperator, getFilterValue],
  filterItems
);

export const getSortedOutlets = createSelector(
  [getFilteredOutlets, sortAttribute, sortAttributeInverted],
  (outlets, attribute, inverted) => {
    return [ ...outlets ].sort((a, b) => {
      if (a[attribute] < b[attribute]) return inverted ? 1 : -1;
      if (a[attribute] > b[attribute]) return inverted ? -1 : 1;
      return 0;
    });
  }
);

export const getPageOfOutlets = createSelector(
  [getSortedOutlets, page, pageSize], (outlets, page, size) => {
    const start = (page - 1) * size;
    const end = page * size;
    return outlets.slice(start, end);
  }
);

export const getOutletsObj = createSelector(
  outlets, outlets => outlets
);

export const getOutletCount = createSelector(
  getFilteredOutlets, outlets => outlets.length
);

export const getOutletSortAttribute = createSelector(
  sortAttribute, sort => sort
);

export const getOutletSortAttributeInverted = createSelector(
  sortAttributeInverted, inverted => inverted
);

export default reducer;
